import { Injectable, inject } from "@angular/core";
import { BehaviorSubject, distinctUntilChanged, Observable, switchMap } from "rxjs";
import { Currency, CurrencyId } from "../models/currency";
import { CurrencyConversionService } from "./currency-conversion.service";
import { CSP } from "../models/csp-api";
import { LoggerService } from "./logger.service";

type CurrencyStateKey = CSP | "azure_mca" | "azure_ca";

/**
 * CurrencyStateService manages currency state per Cloud Service Provider (CSP).
 *
 * Each CSP has a default currency:
 * - AWS: USD
 * - GCP: USD
 * - Azure: GBP
 * - OCI: EUR
 * - DRCC: EUR
 *
 * Users can override the default currency, which is persisted to localStorage.
 */
@Injectable({
  providedIn: "root",
})
export class CurrencyStateService {
  private conversion = inject(CurrencyConversionService);
  private log = inject(LoggerService);
  private readonly activeCsp$ = new BehaviorSubject<CSP>("aws");

  private readonly CURRENCY_PREFS_KEY = "currency";
  private hasInitializedStorage = false;

  public readonly defaultPreferences: Record<CurrencyStateKey, CurrencyId> = {
    aws: "USD",
    gcp: "USD",
    azure: "GBP",
    azure_mca: "USD",
    azure_ca:"USD",
    oci: "EUR",
    drcc: "EUR",
  };

  // CSP-specific currency state
  private readonly currencyState = new Map<CurrencyStateKey, BehaviorSubject<Currency>>([
    ["aws", new BehaviorSubject(this.getInitialCurrency("USD", "aws"))],
    ["gcp", new BehaviorSubject(this.getInitialCurrency("USD", "gcp"))],
    ["azure", new BehaviorSubject(this.getInitialCurrency("GBP", "azure"))],
    ["azure_mca", new BehaviorSubject(this.getInitialCurrency("USD", "azure_mca"))],
    ["azure_ca", new BehaviorSubject(this.getInitialCurrency("USD", "azure_ca"))],
    ["oci", new BehaviorSubject(this.getInitialCurrency("EUR", "oci"))],
    ["drcc", new BehaviorSubject(this.getInitialCurrency("EUR", "drcc"))],
  ]);

  /**
   * Get the storage key for a specific CSP
   */
  /**
   * Load all currency preferences from localStorage
   */
  private loadAllPreferences(): Partial<Record<CurrencyStateKey, CurrencyId>> {
    try {
      const stored = localStorage.getItem(this.CURRENCY_PREFS_KEY);
      if (stored) {
        return JSON.parse(stored);
      }
    } catch (e) {
      this.log.error("Failed to parse currency preferences, using defaults", e);
    }
    return {};
  }

  /**
   * Save all currency preferences to localStorage
   */
  private saveAllPreferences(prefs: Record<CurrencyStateKey, CurrencyId>): void {
    try {
      localStorage.setItem(this.CURRENCY_PREFS_KEY, JSON.stringify(prefs));
    } catch (e) {
      this.log.error("Failed to save currency preferences", e);
    }
  }

  /**
   * Migrate old format (currency-csp keys) to new format (single JSON key)
   */
  private migrateOldFormat(): void {
    const csps: CurrencyStateKey[] = ["aws", "azure", "azure_mca", "gcp", "oci", "drcc"];
    const oldKeys = csps
      .map((csp) => ({
        csp,
        val: localStorage.getItem(`currency-${csp}`),
      }))
      .filter(({ val }) => val !== null);

    if (oldKeys.length > 0) {
      const prefs = Object.fromEntries(oldKeys.map(({ csp, val }) => [csp, val])) as Record<
        CurrencyStateKey,
        CurrencyId
      >;

      this.saveAllPreferences(prefs);

      // Remove old keys
      oldKeys.forEach(({ csp }) => {
        localStorage.removeItem(`currency-${csp}`);
      });

      this.log.info(`Migrated ${oldKeys.length} currency preferences to new format`);
    }
  }

  /**
   * Get the initial currency from localStorage or the default for a CSP
   */
  private getInitialCurrency(defaultCurrencyId: CurrencyId, csp: CurrencyStateKey): Currency {
    // Migrate old format on first load
    this.migrateOldFormat();

    const prefs = this.loadAllPreferences();
    const storedCurrencyId = prefs[csp];
    const currencyId = storedCurrencyId ?? defaultCurrencyId;
    return this.conversion.getCurrency(currencyId, defaultCurrencyId);
  }

  /**
   * Get currency state as Observable for a specific CSP
   */
  public getCurrency$(csp: CurrencyStateKey): Observable<Currency> {
    return (this.currencyState.get(csp) ?? this.currencyState.get("aws"))!.asObservable();
  }

  /**
   * Get the current currency value for a CSP (synchronous)
   */
  public getCurrency(csp: CurrencyStateKey): Currency {
    return (this.currencyState.get(csp) ?? this.currencyState.get("aws"))!.getValue();
  }

  public setActiveCsp(csp: CSP): void {
    this.activeCsp$.next(csp);
  }

  public getActiveCsp(): CSP {
    return this.activeCsp$.getValue();
  }

  /**
   * Get currency state as Observable for the currently active CSP
   */
  public getActiveCurrency$(): Observable<Currency> {
    return this.activeCsp$.pipe(
      distinctUntilChanged(),
      switchMap((csp) => this.getCurrency$(csp)),
      distinctUntilChanged((prev, curr) => prev?.id === curr?.id),
    );
  }

  /**
   * Get the current currency value for the currently active CSP
   */
  public getActiveCurrencyValue(): Currency {
    return this.getCurrency(this.getActiveCsp());
  }

  /**
   * Change currency for a specific CSP only
   * Updates localStorage (per-CSP key) and that CSP's state only
   */
  public changeCurrency(csp: CurrencyStateKey, currencyId: CurrencyId, persist: boolean = true): void {
    this.log.info(`Changing currency for ${csp} to ${currencyId} (persist=${persist})`);

    if (persist) {
      // Load current preferences, update, and save
      const prefs = this.loadAllPreferences();
      prefs[csp] = currencyId;
      this.saveAllPreferences(prefs as Record<CurrencyStateKey, CurrencyId>);
    }

    // Update state for the specific CSP only
    this.updateCurrencyForCsp(csp, currencyId);
  }

  /**
   * Update currency state for a specific CSP
   */
  private updateCurrencyForCsp(csp: CurrencyStateKey, currencyId: CurrencyId): void {
    const defaultForCsp = this.getDefaultCurrencyForCsp(csp);
    const newCurrency = this.conversion.getCurrency(currencyId, defaultForCsp);
    this.currencyState.get(csp)?.next(newCurrency);
  }

  /**
   * Get the default currency for a CSP
   */
  private getDefaultCurrencyForCsp(csp: CurrencyStateKey): CurrencyId {
    const defaults: Record<CurrencyStateKey, CurrencyId> = {
      aws: "USD",
      gcp: "USD",
      azure: "GBP",
      azure_mca: "USD",
      azure_ca: "USD",
      oci: "EUR",
      drcc: "EUR",
    };
    return defaults[csp] ?? "USD";
  }

  private isCurrencyId(value: unknown): value is CurrencyId {
    return value === "USD" || value === "GBP" || value === "EUR";
  }

  private normalizePreferencesObject(raw: unknown): Record<CurrencyStateKey, CurrencyId> {
    const normalized: Record<CurrencyStateKey, CurrencyId> = {
      ...this.defaultPreferences,
    };

    if (!raw || typeof raw !== "object") {
      return normalized;
    }

    const candidate = raw as Partial<Record<CurrencyStateKey, unknown>>;
    const csps = Object.keys(this.defaultPreferences) as CurrencyStateKey[];

    for (const csp of csps) {
      const value = candidate[csp];
      if (this.isCurrencyId(value)) {
        normalized[csp] = value;
      }
    }

    return normalized;
  }

  private ensureStorageInitialized(): void {
    if (this.hasInitializedStorage) {
      return;
    }

    this.migrateOldFormat();

    const raw = localStorage.getItem(this.CURRENCY_PREFS_KEY);
    const fallback = { ...this.defaultPreferences };

    if (!raw) {
      this.saveAllPreferences(fallback);
      this.hasInitializedStorage = true;
      return;
    }

    try {
      const parsed = JSON.parse(raw);

      if (typeof parsed === "string" && this.isCurrencyId(parsed)) {
        const migrated = { ...fallback, aws: parsed };
        this.saveAllPreferences(migrated);
        this.log.info("Migrated legacy currency string to object format");
      } else {
        const normalized = this.normalizePreferencesObject(parsed);
        this.saveAllPreferences(normalized);
      }
    } catch (e) {
      const legacy = raw.replace(/^"|"$/g, "");
      if (this.isCurrencyId(legacy)) {
        const migrated = { ...fallback, aws: legacy };
        this.saveAllPreferences(migrated);
        this.log.info("Migrated legacy raw currency value to object format");
      } else {
        this.log.error("Invalid currency storage format, resetting to defaults", e);
        this.saveAllPreferences(fallback);
      }
    }

    this.hasInitializedStorage = true;
  }

  public initializeOnAppStart(): void {
    this.ensureStorageInitialized();
  }
}
